/**
 * WAV encode / header parse / linear resample — pure, runs in browser and Node.
 * §9 母带规范: 48 kHz WAV upload; server re-parses the header to enforce the
 * 22 s hard limit before accepting a recording.
 */

/** §9 hard upper limit for one ordinary recording (also the training window). */
export const MAX_DURATION_MS = 22_000;

/**
 * Absolute ceiling for a per-material override (`material.max_duration_ms`).
 * 气息 items (持续发 si、一口气绕口令) need more than §9's 22 s, but nothing may
 * ever exceed this — and anything above {@link MAX_DURATION_MS} is a measurement,
 * never a training row (see `splitForExport`).
 */
export const MAX_DURATION_CAP_MS = 45_000;

/** The per-material cap, defaulted and clamped into `[1, MAX_DURATION_CAP_MS]`. */
export function effectiveMaxDurationMs(override?: number | null): number {
  if (typeof override !== "number" || !Number.isFinite(override) || override <= 0) {
    return MAX_DURATION_MS;
  }
  return Math.min(Math.round(override), MAX_DURATION_CAP_MS);
}

export const TARGET_SAMPLE_RATE = 48_000;

export class WavError extends Error {}

export interface WavHeader {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  durationMs: number;
  dataBytes: number;
}

function clamp(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

/**
 * Encode float32 channel data as a 16-bit PCM mono WAV (channels are downmixed
 * by averaging). Returns the full RIFF buffer.
 */
export function encodeWav(float32Channels: Float32Array[], sampleRate: number): ArrayBuffer {
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) {
    throw new WavError(`invalid sampleRate: ${sampleRate}`);
  }
  if (float32Channels.length === 0) throw new WavError("no channels supplied");
  const nCh = float32Channels.length;
  const len = Math.max(...float32Channels.map((c) => c.length));
  const buf = new ArrayBuffer(44 + len * 2);
  const v = new DataView(buf);
  const wstr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i));
  };
  wstr(0, "RIFF");
  v.setUint32(4, 36 + len * 2, true);
  wstr(8, "WAVE");
  wstr(12, "fmt ");
  v.setUint32(16, 16, true); // PCM fmt chunk size
  v.setUint16(20, 1, true); // format = PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true); // byte rate = rate * blockAlign
  v.setUint16(32, 2, true); // block align
  v.setUint16(34, 16, true); // bits per sample
  wstr(36, "data");
  v.setUint32(40, len * 2, true);
  let off = 44;
  for (let i = 0; i < len; i++, off += 2) {
    let s = 0;
    for (let c = 0; c < nCh; c++) s += (float32Channels[c]![i] ?? 0) / nCh;
    s = clamp(s);
    v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

function toView(buf: ArrayBuffer | ArrayBufferView): DataView {
  if (ArrayBuffer.isView(buf)) {
    return new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  return new DataView(buf);
}

function readTag(v: DataView, off: number): string {
  return String.fromCharCode(
    v.getUint8(off),
    v.getUint8(off + 1),
    v.getUint8(off + 2),
    v.getUint8(off + 3),
  );
}

/** Parse and validate a RIFF/WAVE header (walks chunks; handles extra chunks). */
export function parseWavHeader(buf: ArrayBuffer | ArrayBufferView): WavHeader {
  const v = toView(buf);
  if (v.byteLength < 44) throw new WavError("buffer too small to be a WAV file");
  if (readTag(v, 0) !== "RIFF") throw new WavError("missing RIFF magic");
  if (readTag(v, 8) !== "WAVE") throw new WavError("missing WAVE magic");

  let off = 12;
  let fmt: { channels: number; sampleRate: number; bitsPerSample: number } | null = null;
  let dataBytes: number | null = null;
  while (off + 8 <= v.byteLength) {
    const id = readTag(v, off);
    const size = v.getUint32(off + 4, true);
    const body = off + 8;
    if (id === "fmt ") {
      if (size < 16) throw new WavError("fmt chunk too small");
      const audioFormat = v.getUint16(body, true);
      if (audioFormat !== 1 && audioFormat !== 0xfffe) {
        throw new WavError(`unsupported WAV format ${audioFormat} (expected PCM)`);
      }
      fmt = {
        channels: v.getUint16(body + 2, true),
        sampleRate: v.getUint32(body + 4, true),
        bitsPerSample: v.getUint16(body + 14, true),
      };
    } else if (id === "data") {
      dataBytes = Math.min(size, v.byteLength - body);
    }
    off = body + size + (size % 2); // chunks are word-aligned
  }
  if (!fmt) throw new WavError("no fmt chunk");
  if (dataBytes === null) throw new WavError("no data chunk");
  if (fmt.channels < 1) throw new WavError("invalid channel count");
  if (fmt.sampleRate <= 0) throw new WavError("invalid sample rate");
  if (![8, 16, 24, 32].includes(fmt.bitsPerSample)) {
    throw new WavError(`unsupported bitsPerSample ${fmt.bitsPerSample}`);
  }
  const bytesPerFrame = (fmt.bitsPerSample / 8) * fmt.channels;
  const durationMs = Math.round((dataBytes / bytesPerFrame / fmt.sampleRate) * 1000);
  return { ...fmt, dataBytes, durationMs };
}

/** Decode a 16-bit PCM mono WAV body back to Float32 (test / QC helper). */
export function decodeWavMono(buf: ArrayBuffer | ArrayBufferView): Float32Array {
  const header = parseWavHeader(buf);
  if (header.bitsPerSample !== 16) throw new WavError("decodeWavMono expects 16-bit PCM");
  const v = toView(buf);
  let off = 12;
  let dataOff = -1;
  let size = 0;
  while (off + 8 <= v.byteLength) {
    const id = readTag(v, off);
    const chunkSize = v.getUint32(off + 4, true);
    if (id === "data") {
      dataOff = off + 8;
      size = Math.min(chunkSize, v.byteLength - dataOff);
      break;
    }
    off = off + 8 + chunkSize + (chunkSize % 2);
  }
  if (dataOff < 0) throw new WavError("no data chunk");
  const nCh = header.channels;
  const frames = Math.floor(size / 2 / nCh);
  const out = new Float32Array(frames);
  for (let i = 0; i < frames; i++) {
    let acc = 0;
    for (let c = 0; c < nCh; c++) acc += v.getInt16(dataOff + (i * nCh + c) * 2, true) / 0x8000;
    out[i] = acc / nCh;
  }
  return out;
}

/** Linear-interpolation resample. */
export function resample(float32: Float32Array, from: number, to: number): Float32Array {
  if (from <= 0 || to <= 0) throw new WavError("invalid sample rates for resample");
  if (from === to) return Float32Array.from(float32);
  const len = float32.length;
  if (len === 0) return new Float32Array(0);
  const ratio = from / to;
  const outLen = Math.max(1, Math.floor(len / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const src = i * ratio;
    const i0 = Math.floor(src);
    const i1 = Math.min(i0 + 1, len - 1);
    const frac = src - i0;
    out[i] = (float32[i0] ?? 0) * (1 - frac) + (float32[i1] ?? 0) * frac;
  }
  return out;
}

/** §9 hard limit check against a parsed header. */
export function durationWithinLimit(header: WavHeader, maxMs = MAX_DURATION_MS): boolean {
  return header.durationMs <= maxMs;
}

/** MiMo scoring rate. 48 kHz masters are downsampled; 16 kHz stays 16 kHz. */
export const AI_AUDIO_RATE = 16_000;

/**
 * 16-bit PCM WAV → mono 16 kHz WAV. Channels are averaged by {@link decodeWavMono}.
 * Used only to build the scoring payload; the stored master is never rewritten.
 */
export function toMono16kWav(wav: Uint8Array): Uint8Array {
  const header = parseWavHeader(wav);
  if (header.bitsPerSample !== 16) throw new WavError("toMono16kWav expects 16-bit PCM");
  const pcm = decodeWavMono(wav);
  const out = resample(pcm, header.sampleRate, AI_AUDIO_RATE);
  return new Uint8Array(encodeWav([out], AI_AUDIO_RATE));
}
